(ns story-builder.showcase.view
  (:require
   [camel-snake-kebab.core :as csk]
   [camel-snake-kebab.extras :as cske]
   [clojure.set]
   [clojure.string :as str]
   [re-frame.core :as rf]
   [story-builder.component.wrapper :as wrapper]
   [story-builder.define :as define]
   [story-builder.event.fx]
   [story-builder.event.request]
   [story-builder.event.util :as event.util]
   [story-builder.story-list.sidebar.util :as story-list.sidebar.util]
   [story-builder.translation :refer [tr]]
   [story-builder.util.core :as util]))

(defn make-transform-url [url]
  (str "https://transform-cf1.nws.ai/" (js/encodeURIComponent url) "/compress/"))

;; Subs

(rf/reg-sub
 ::showcase
 (fn [db]
   (:showcase db)))

(rf/reg-sub
 ::projects
 :<- [::showcase]
 (fn [showcase]
   (:projects showcase)))

(rf/reg-sub
 ::pagination
 (fn [db]
   (:pagination db)))

(rf/reg-sub
 ::search-query
 (fn [db]
   (:search-query db)))

(rf/reg-sub
 ::filters
 (fn [db]
   (:filters db)))

;; Events

(rf/reg-event-fx
 ::apply-filters
 (fn [{:keys [db]} _]
   (let [pending-filters (-> db :filters :pending)]
     {:db (assoc-in db [:filters :applied] pending-filters)
      :fx [[:merge-query-string pending-filters]
           [:dispatch [::set-page 1]]
           [:dispatch [::close-filters]]
           [:dispatch [::fetch-showcase]]]})))

(rf/reg-event-fx
 ::clear-filters
 (fn [_ _]
   {:fx [[:dispatch [::set-filter-path [:pending :feature] []]]
         [:dispatch [::set-filter-path [:pending :industry] []]]
         [:dispatch [::set-filter-path [:pending :layer-type] []]]
         [:dispatch [::set-filter-path [:pending :locale] []]]]}))

(rf/reg-event-fx
 ::set-filter-path
 (fn [{:keys [db]} [_ path value]]
   {:db (assoc-in db (into [:filters] path) value)}))

(rf/reg-event-db
 ::close-filters
 (fn [db _]
   (update db :filters dissoc :open?)))

(rf/reg-event-db
 ::toggle-filters
 (fn [db _]
   (update-in db [:filters :open?] not)))

(rf/reg-event-db
 :ui/loading-local
 (fn [db [_ {:keys [id cancel]}]]
   (let [loading? (get-in db [:ui/loading-local id])]
     (update db :ui/loading-local
             (fn [loading-local]
               (if loading?
                 (dissoc loading-local id)
                 (assoc loading-local id cancel)))))))

(rf/reg-sub
 :ui/loading-local
 (fn [db [_ id]]
   (get-in db [:ui/loading-local id]
           (let [keys (keys (get-in db [:ui/loading-local]))]
             (when (contains? (->> keys
                                   (filter vector?)
                                   (map first)
                                   (set))
                              id)
               true)))))

(rf/reg-event-db
 ::receive-showcase
 (fn [db [_ showcase]]
   (let [pagination (select-keys (:projects showcase) [:count :next :previous :page-size])
         projects   (-> showcase :projects :results)]
     (-> db
         (assoc :showcase (assoc showcase :projects projects))
         (update :pagination merge pagination)))))

(defn on-fetch-showcase-response [r]
  (rf/dispatch [::receive-showcase (->> (:body r)
                                        (cske/transform-keys csk/->kebab-case))]))

(rf/reg-event-fx
 ::fetch-showcase
 (fn [{:keys [db]} _]
   (let [{:keys [filters
                 pagination
                 search-query
                 website-alias
                 website-id]} db
         page                 (:page pagination)
         {:keys [applied]}    filters
         {:keys [feature
                 industry
                 layer-type
                 locale]}     applied]
     {:dispatch [:make-request
                 {:method       "GET"
                  :url          (str (:cdnapi define/URLs) "/v1/showcase/" (or website-alias website-id) "/")
                  :custom-opts  {:query-params {:feature    (when feature (str/join "," feature))
                                                :industry   (when industry (str/join "," industry))
                                                :layer-type (when layer-type (str/join "," layer-type))
                                                :locale     (when locale (str/join "," locale))
                                                :page       page
                                                :search     search-query}}
                  :local-loader ::fetch-showcase
                  :on-response  {:200 on-fetch-showcase-response}}]})))

(rf/reg-event-fx
 ::set-search-query
 (fn [{:keys [db]} [_ query]]
   {:db (assoc db :search-query query)
    :fx [[:dispatch [::set-page 1]]
         [:merge-query-string {:search query}]]}))

(rf/reg-event-fx
 ::next-page
 (fn [{:keys [db]} _]
   (let [page        (get-in db [:pagination :page] 1)
         total-pages (dec (mod (-> db :pagination :count) (-> db :pagination :page-size)))]
     {:fx [[:dispatch [::set-page (min (inc page) total-pages)]]]})))

(rf/reg-event-fx
 ::prev-page
 (fn [{:keys [db]} _]
   (let [page (get-in db [:pagination :page] 1)]
     {:fx [[:dispatch [::set-page (max (dec page) 0)]]]})))

(rf/reg-event-fx
 ::set-page
 (fn [{:keys [db]} [_ page]]
   {:db (assoc-in db [:pagination :page] page)
    :fx [[:dispatch [::fetch-showcase]]
         [:merge-query-string {:page page}]]}))

(defn search-handler [e]
  (let [query (.. e -target -value)]
    (event.util/dispatch-debounce [::set-search-query query])))

(def pages-to-show 3)

(defn pagination-component [pagination]
  (let [{:keys [count
                page
                page-size]} pagination
        total-pages        (js/Math.ceil (/ count page-size))
        first-page?        (= page 1)
        last-page?         (= page total-pages)
        half-pages-to-show (js/Math.floor (/ pages-to-show 2))
        start-page         (max 1 (- page half-pages-to-show))
        end-page           (min total-pages (+ start-page pages-to-show -1))
        show-start-ellipsis (> start-page 1)
        show-end-ellipsis   (< end-page total-pages)]
    (when (> count page-size)
      [:nav.u-d-f.u-fxd-r.u-ai-c
       [:ul.Pagination.is-left.u-mt-40px.u-mb-10px
        [:li.Pagination-item.Pagination-item--prev
         [:button.Pagination-link
          {:class    [(when first-page? "u-disabled")]
           :on-click #(rf/dispatch [::prev-page])
           :type     "button"}
          (tr [:prev-page "Prev"])]]

        (when show-start-ellipsis
          [:<>
           [:li.Pagination-item
            [:button.Pagination-link
             {:on-click #(rf/dispatch [::set-page 1])
              :type     "button"}
             1]]
           (when (> start-page 2)
             [:li.Pagination-item [:span "..."]])])

        (for [p (range start-page (inc end-page))]
          ^{:key p}
          [:li.Pagination-item
           {:class    [(when (= p page) "is-active")
                       (when (> p total-pages) "u-disabled")]
            :on-click (when-not (= p page) #(rf/dispatch [::set-page p]))}
           [:button.Pagination-link
            {:on-click #(rf/dispatch [::set-page p])
             :type     "button"}
            p]])

        (when show-end-ellipsis
          [:<>
           (when (< end-page (- total-pages 1))
             [:li.Pagination-item [:span "..."]])
           [:li.Pagination-item
            [:button.Pagination-link
             {:on-click #(rf/dispatch [::set-page total-pages])
              :type     "button"}
             total-pages]]])

        [:li.Pagination-item.Pagination-item--next
         [:button.Pagination-link
          {:class    [(when last-page? "u-disabled")]
           :on-click #(rf/dispatch [::next-page])
           :type     "button"}
          (tr [:next-page "Next"])]]]])))

(def all-industry-options
  [{:name  "Auto"
    :value "Auto"}
   {:name  "Beauty"
    :value "Beauty"}
   {:name  "CPG"
    :value "CPG"}
   {:name  "Decor"
    :value "Decor"}
   {:name  "Drinks"
    :value "Drinks"}
   {:name  "Ecommerce"
    :value "Ecommerce"}
   {:name  "Editorial"
    :value "Editorial"}
   {:name  "Electronics"
    :value "Electronics"}
   {:name  "Entertainment"
    :value "Entertainment"}
   {:name  "Fashion"
    :value "Fashion"}
   {:name  "Finance"
    :value "Finance"}
   {:name  "Food"
    :value "Food"}
   {:name  "Health"
    :value "Health"}
   {:name  "Home"
    :value "Home"}
   {:name  "Insurance"
    :value "Insurance"}
   {:name  "Lifestyle"
    :value "Lifestyle"}
   {:name  "Luxury"
    :value "Luxury"}
   {:name  "Retail"
    :value "Retail"}
   {:name  "Technology"
    :value "Technology"}
   {:name  "Travel"
    :value "Travel"}])

(def all-layer-type-options
  [{:name  "Poll"
    :value "poll"}
   {:name  "Image poll"
    :value "image-poll"}
   {:name  "Quiz"
    :value "quiz"}
   {:name  "Image quiz"
    :value "image-quiz"}
   {:name  "Slider"
    :value "slider"}
   {:name  "Embed"
    :value "embed"}
   {:name  "Video"
    :value "video"}
   {:name  "Scratch"
    :value "scratch"}
   {:name  "Reveal"
    :value "reveal"}
   {:name  "Slideshow"
    :value "slideshow"}
   {:name  "Attachment"
    :value "attachment"}])

(def all-feature-options
  [{:name  "Video analytics"
    :value "video-analytics"}])

(def all-locale-options
  [{:name  "English"
    :value "en"}
   {:name  "Bengali"
    :value "bn"}
   {:name  "Dutch"
    :value "nl"}
   {:name  "French"
    :value "fr"}
   {:name  "German"
    :value "de"}
   {:name  "Hindi"
    :value "hi"}
   {:name  "Indonesian"
    :value "id"}
   {:name  "Italian"
    :value "it"}
   {:name  "Polish"
    :value "pl"}
   {:name  "Portuguese"
    :value "pt"}
   {:name  "Spanish"
    :value "es"}
   {:name  "Romanian"
    :value "ro"}])

(defn filters []
  (let [{:keys [applied
                pending
                open?]}          @(rf/subscribe [::filters])
        industry-selection       (into (clojure.set/intersection (set (:industry applied)) (set (:industry pending)))
                                       (:industry pending))
        industry-selection       (mapv (fn [v] {:value v :name (:name (util/find-first #(= (:value %) v) all-industry-options))}) industry-selection)
        layer-type-selection     (into (clojure.set/intersection (set (:layer-type applied)) (set (:layer-type pending)))
                                       (:layer-type pending))
        layer-type-selection     (mapv (fn [v] {:value v :name (:name (util/find-first #(= (:value %) v) all-layer-type-options))}) layer-type-selection)
        feature-selection        (into (clojure.set/intersection (set (:feature applied)) (set (:feature pending)))
                                       (:feature pending))
        feature-selection        (mapv (fn [v] {:value v :name (:name (util/find-first #(= (:value %) v) all-feature-options))}) feature-selection)
        locale-selection         (into (clojure.set/intersection (set (:locale applied)) (set (:locale pending)))
                                       (:locale pending))
        locale-selection         (mapv (fn [v] {:value v :name (:name (util/find-first #(= (:value %) v) all-locale-options))}) locale-selection)
        all-industries-selected? (= (count industry-selection)
                                    (count all-industry-options))
        all-features-selected?   (= (+ (count feature-selection)
                                       (count layer-type-selection))
                                    (count all-layer-type-options))
        all-locales-selected?    (= (count locale-selection)
                                    (count all-locale-options))
        filter-count             (apply + [(count feature-selection)
                                           (count industry-selection)
                                           (count layer-type-selection)
                                           (count locale-selection)])]
    [wrapper/click-outside-wrapper
     {:on-click-outside #(rf/dispatch [::close-filters])}
     [:div.Dropdown.u-miw-100px.u-w-150px.u-ml-15px.ShowcasePage-filtersDropdown
      {:class (when open? "is-open")}
      [:button.Dropdown-toggle.Button.Button--light.is-filled.is-formEl.is-rounded.u-p-0.u-pl-20px.u-pr-20px
       {:on-click #(rf/dispatch [::toggle-filters])}
       [:span
        [:span.u-d-f.u-ai-c
         [:span.Button-text
          (tr [:filters "Filters"])]
         (when-not (zero? filter-count)
           [:span.Badge.Badge--primary-100.u-m-0.u-fz-12px.is-rounded.u-ml-7px
            filter-count])]
        (util/icon "tune" "is-smallmed" "u-ml-10px")]]
      [:div.Dropdown-content.is-right
       [:div.Dropdown-header.is-sep
        (if (zero? filter-count)
          [:span.u-fz-12px
           (tr [:no-filters-selected "No filters selected"])]
          [:<>
           [:span.u-fz-12px
            (if (= filter-count 1)
              (tr [:1-filter-selected "1 filter selected"])
              (tr [:n-filters-selected "%1 filters selected"] [filter-count]))]
           [:button.Button.Button--neutral-900.is-text.u-h-a.u-p-0.u-m-0.u-ml-a.u-td-u
            {:title    (tr [:clear-all "Clear all"])
             :on-click #(rf/dispatch [::clear-filters])}
            [:span (tr [:clear-all "Clear all"])]]])]
       [:div.Dropdown-body
        [:div.FormCol
         [:div.FormGroup.u-pb-10px.is-borderless
          [:div.u-d-f.u-ai-c.u-jc-sb.u-mb-20px
           [:label.FormLabel (tr [:industries/field-label "Industries:"])]
           [:button.Button.Button--neutral-200.is-xsmall.is-outlined.is-hoverBg.u-m-0.u-ml-a.u-c-dark.u-fz-11px
            {:on-click (fn [_]
                         (rf/dispatch [::set-filter-path [:pending :industry]
                                       (if all-industries-selected?
                                         []
                                         (->> (map :value all-industry-options)
                                              (vec)))]))}
            [:span
             (if all-industries-selected?
               (tr [:deselect-all "Deselect all"])
               (tr [:select-all "Select all"]))]]]
          [:div.FormGroup-row.u-fxw-w.u-w-250px
           (for [{:keys [name value]} all-industry-options]
             (let [id (str "industry-checkbox-" value)]
               ^{:key value}
               [:div.FormCheck.is-smallIcon.is-blueCheckedBg.u-col-6.u-pr-20px.u-mr-0
                [:input
                 {:type      "checkbox"
                  :id        id
                  :on-change (fn [e]
                               (let [checked? (.. e -target -checked)]
                                 (rf/dispatch [::set-filter-path [:pending :industry]
                                               (if checked?
                                                 ((fnil conj []) (map :value industry-selection) value)
                                                 (->> (map :value industry-selection)
                                                      (remove #(= value %))
                                                      (vec)))])))
                  :checked   (contains? (set (map :value industry-selection)) value)}]
                [:label {:html-for id}
                 name]]))]]
         [:div.FormGroup.u-pb-10px.is-borderless.u-mt-20px
          [:div.u-d-f.u-ai-c.u-jc-sb.u-mb-20px
           [:label.FormLabel (tr [:features/field-label "Features:"])]
           [:button.Button.Button--neutral-200.is-xsmall.is-outlined.is-hoverBg.u-m-0.u-ml-a.u-c-dark.u-fz-11px
            {:on-click (fn [_]
                         (rf/dispatch [::set-filter-path [:pending :layer-type]
                                       (if all-features-selected?
                                         []
                                         (->> (map :value all-layer-type-options)
                                              (vec)))]))}
            [:span
             (if all-features-selected?
               (tr [:deselect-all "Deselect all"])
               (tr [:select-all "Select all"]))]]]
          [:div.FormGroup-row.u-fxw-w.u-w-250px
           (for [{:keys [name value]} all-layer-type-options]
             (let [id (str "feature-checkbox-" value)]
               ^{:key value}
               [:div.FormCheck.is-smallIcon.is-blueCheckedBg.u-col-6.u-pr-20px.u-mr-0
                [:input
                 {:type      "checkbox"
                  :id        id
                  :on-change (fn [e]
                               (let [checked? (.. e -target -checked)]
                                 (rf/dispatch [::set-filter-path [:pending :layer-type]
                                               (if checked?
                                                 ((fnil conj []) (map :value layer-type-selection) value)
                                                 (->> (map :value layer-type-selection)
                                                      (remove #(= value %))
                                                      (vec)))])))
                  :checked   (contains? (set (map :value layer-type-selection)) value)}]
                [:label {:html-for id}
                 name]]))
           (for [{:keys [name value]} all-feature-options]
             (let [id (str "feature-checkbox-" value)]
               ^{:key value}
               [:div.FormCheck.is-smallIcon.is-blueCheckedBg.u-col-6.u-pr-20px.u-mr-0
                [:input
                 {:type      "checkbox"
                  :id        id
                  :on-change (fn [e]
                               (let [checked? (.. e -target -checked)]
                                 (rf/dispatch [::set-filter-path [:pending :feature]
                                               (if checked?
                                                 ((fnil conj []) (map :value feature-selection) "video-analytics")
                                                 (->> (map :value feature-selection)
                                                      (remove #(= "video-analytics" %))
                                                      (vec)))])))
                  :checked   (contains? (set (map :value feature-selection)) "video-analytics")}]
                [:label {:html-for id}
                 name]]))]]
         [:div.FormGroup.u-pb-10px.is-borderless.u-mt-20px
          [:div.u-d-f.u-ai-c.u-jc-sb.u-mb-20px
           [:label.FormLabel (tr [:locales/field-label "Locales:"])]
           [:button.Button.Button--neutral-200.is-xsmall.is-outlined.is-hoverBg.u-m-0.u-ml-a.u-c-dark.u-fz-11px
            {:on-click (fn [_]
                         (rf/dispatch [::set-filter-path [:pending :locale]
                                       (if all-locales-selected?
                                         []
                                         (->> (map :value all-locale-options)
                                              (vec)))]))}
            [:span
             (if all-locales-selected?
               (tr [:deselect-all "Deselect all"])
               (tr [:select-all "Select all"]))]]]
          [:div.FormGroup-row.u-fxw-w.u-w-250px
           (for [{:keys [name value]} all-locale-options]
             (let [id (str "locale-checkbox-" value)]
               ^{:key value}
               [:div.FormCheck.is-smallIcon.is-blueCheckedBg.u-col-6.u-pr-20px.u-mr-0
                [:input
                 {:type      "checkbox"
                  :id        id
                  :on-change (fn [e]
                               (let [checked? (.. e -target -checked)]
                                 (rf/dispatch [::set-filter-path [:pending :locale]
                                               (if checked?
                                                 ((fnil conj []) (map :value locale-selection) value)
                                                 (->> (map :value locale-selection)
                                                      (remove #(= value %))
                                                      (vec)))])))
                  :checked   (contains? (set (map :value locale-selection)) value)}]
                [:label {:html-for id}
                 name]]))]]]]
       [:div.Dropdown-footer
        [:button.Button.Button--neutral-900.is-filled.is-rounded.u-w-100
         {:class    (when (= pending applied) "u-disabled")
          :title    (tr [:apply-filters "Apply filters"])
          :on-click #(rf/dispatch [::apply-filters])}
         [:span (tr [:apply-filters "Apply filters"])]]]]]]))

(defn showcase
  [{:keys [pagination
           showcase
           search-query
           website]}]
  (let [projects     (or @(rf/subscribe [::projects])
                         (:projects showcase))
        pagination   (or @(rf/subscribe [::pagination])
                         pagination)
        search-query (or @(rf/subscribe [::search-query])
                         search-query)
        loading?     @(rf/subscribe [:ui/loading-local ::fetch-showcase])]
    [:div.MainPage.is-overflow.is-showcase.is-loaded.
     [:div.MainContent
      [:div.MainContent-header.u-ov-v
       [:a.MainContent-header-logo
        {:href ""}
        (if-let [logo (:logo website)]
          [:img {:src (str "https://cdn-cf1.nws.ai/platform/" logo)
                 :alt (:name website)}]
          (util/icon "newsroom-studio"))]
       [:div.u-d-f.u-w-100.u-ml-a
        [:div.FormEl.is-rounded.u-w-100.u-maw-300px.u-ml-a
         [:input.u-w-100.u-p-0.u-pl-20px.u-pr-20px
          {:type          "search"
           :default-value search-query
           :placeholder   (tr [:search-by-brand-or-title "Search by brand or title..."])
           :on-change     search-handler}]]
        [filters]]]
      (if loading?
        [:div.Loader.is-fixed.is-darkBg.is-transparentBg.is-active
         [:div
          {:data-loader "ball-fade"}]]
        [:<>
         [:div.MainContent-body
          [:div.MainContent-body-section.u-w-100
           [:div.MainContent-body-section-content
            [:div.Gallery.is-cards.Gallery--showcase
             [:div.Gallery-items
              (doall
               (for [{:keys [id
                             screenshot
                             title
                             url]} projects]
                 ^{:key id}
                 [:div.Gallery-item
                  [:div.Gallery-item-visual
                   [:img {:src     (make-transform-url screenshot)
                          :alt     title
                          :loading "lazy"}]]
                  [:div.Gallery-item-controls.u-group.u-jc-c
                   [:a.u-pos-a.u-t-0.u-l-0.ItemHoverBtn
                    {:href   url
                     :target "_blank"}]
                   [:div.u-pos-a.u-t-0.u-l-0.u-w-100.u-h-100.u-d-f.u-ai-c.u-jc-c.u-op-0.u-group-hoverEl-op-1.u-poev-n
                    [:a.Button.Button--light.is-outlined.is-med.is-rounded.is-lightHoverBg.is-noHoverBd.u-pos-r.u-p-10px.u-poev-a
                     {:href   url
                      :target "_blank"}
                     [:span.u-ff-regular.u-fw-regular.u-fz-11px (tr [:open-in-new-tab "Open in new tab"])]]]
                   [:div.u-d-f.u-jc-c.u-w-100.u-mt-a.u-op-0.u-group-hoverEl-op-1
                    [:button.Button.Button--light.is-filled.is-small.is-rounded.is-darkHoverBg.is-noHoverBd.u-pos-r.u-p-10px
                     {:on-click (fn [_]
                                  (js/window.open (str "in-feed/?tag="
                                                       (-> (story-list.sidebar.util/embed-code
                                                            :dv360
                                                            {:title title
                                                             :url   url}
                                                            {:frame-type   :friendly
                                                             :config-only? true
                                                             :force-url?   true})
                                                           (assoc :clickURL "")
                                                           (clj->js)
                                                           (js/JSON.stringify)
                                                           (js/encodeURIComponent)))
                                                  "_blank" "noopener noreferrer"))}
                     [:span.u-ff-regular.u-fw-regular.u-fz-11px (tr [:preview-in-feed "Preview in feed"])]]]]]))]]
            [pagination-component pagination]]]]
         [:div.MainContent-footer
          [:a.u-mt-5px.u-mb-5px.u-ff-medium.u-fw-medium
           {:href   "https://www.nws.ai"
            :title  (tr [:powered-by-newsroom-ai "Powered by Newsroom AI"])
            :target "_blank"}
           (tr [:powered-by-newsroom-ai "Powered by Newsroom AI"])]
          [:span.u-mt-5px.u-mb-5px
           {:dangerouslySetInnerHTML {:__html (str "© " (.getFullYear (new js/Date)) " Copyright - All rights reserved")}}]]])]]))